import type { InterviewSummary } from "@/api/generated/types";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

const RADIUS = 18;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

interface AiScoreCircleProps {
  score: number | null | undefined;
  status: InterviewSummary["status"];
}

function getStrokeClass(score: number): string {
  if (score >= 80) {
    return "text-teal-500";
  }
  if (score >= 60) {
    return "text-teal-500/80";
  }
  return "text-slate-400";
}

export function AiScoreCircle({ score, status }: AiScoreCircleProps) {
  if (typeof score !== "number") {
    if (status !== "EVALUATING") {
      return (
        <span data-testid="ai-score-empty" aria-label="未評価" className="text-sm text-slate-300">
          —
        </span>
      );
    }
    return (
      <div
        data-testid="ai-score-analyzing"
        className="inline-flex items-center gap-1.5 bg-slate-100 px-2 py-1 rounded-full border border-slate-200"
      >
        <Loader2 className="size-3.5 text-slate-400 animate-spin" />
        <span className="text-[10px] font-medium text-slate-600">評価中</span>
      </div>
    );
  }

  const clamped = Math.max(0, Math.min(100, Math.round(score)));
  const dashOffset = CIRCUMFERENCE * (1 - clamped / 100);
  const strokeClass = getStrokeClass(clamped);

  return (
    <div
      data-testid="ai-score-circle"
      className="relative inline-flex items-center justify-center"
      aria-label={`AIスコア ${clamped}%`}
    >
      <svg className="w-10 h-10 -rotate-90" viewBox="0 0 40 40" aria-hidden="true">
        <circle
          className="text-slate-100"
          cx="20"
          cy="20"
          r={RADIUS}
          stroke="currentColor"
          fill="transparent"
          strokeWidth="3"
        />
        <circle
          className={cn(strokeClass, "transition-[stroke-dashoffset] duration-500")}
          cx="20"
          cy="20"
          r={RADIUS}
          stroke="currentColor"
          fill="transparent"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={dashOffset}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-[11px] font-bold text-slate-900 tabular-nums">{clamped}%</span>
      </div>
    </div>
  );
}
